import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import type {
  ExecucaoChecklist,
  PaginatedResponse,
  RespostaItem,
  TemplateChecklist,
  TemplateItem,
} from '../types'

interface ExecucaoListParams {
  ordem_servico?: string
  status?: string
  page?: number
}

export function useExecucaoList(params?: ExecucaoListParams) {
  return useQuery({
    queryKey: ['execucoes-checklist', params],
    queryFn: async () => {
      const r = await api.get<PaginatedResponse<ExecucaoChecklist>>('/checklists/execucoes/', {
        params,
      })
      return r.data
    },
    staleTime: 1000 * 30,
  })
}

export function useExecucaoDetalhe(id: string) {
  return useQuery({
    queryKey: ['execucoes-checklist', id],
    queryFn: async () => {
      const r = await api.get<ExecucaoChecklist>(`/checklists/execucoes/${id}/`)
      return r.data
    },
    enabled: !!id,
  })
}

export function useTemplateDetalhe(id: string) {
  return useQuery({
    queryKey: ['templates-checklist', id],
    queryFn: async () => {
      const r = await api.get<TemplateChecklist>(`/checklists/templates/${id}/`)
      return r.data
    },
    enabled: !!id,
  })
}

export function useRespostasList(execucaoId: string) {
  return useQuery({
    queryKey: ['respostas', execucaoId],
    queryFn: async () => {
      const r = await api.get<PaginatedResponse<RespostaItem>>(
        `/checklists/execucoes/${execucaoId}/respostas/`,
      )
      return r.data
    },
    enabled: !!execucaoId,
  })
}

export function useRespostaUpdate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: Partial<RespostaItem>
    }) => {
      const r = await api.patch<RespostaItem>(`/checklists/respostas/${id}/`, data)
      return r.data
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['respostas'] })
      qc.invalidateQueries({ queryKey: ['execucoes-checklist'] })
      void vars
    },
  })
}

export function useExecucaoConcluir(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post(`/checklists/execucoes/${id}/concluir/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['execucoes-checklist'] })
    },
  })
}

// Helper: combina item do template com resposta correspondente
export interface ItemComResposta {
  item: TemplateItem
  resposta: RespostaItem
  /** False quando este item depende de outro e a condição não foi satisfeita. */
  visivel: boolean
}

/**
 * Avalia se um item dependente deve ser visível baseado na resposta do pai.
 * Backend usa essa mesma lógica em ExecucaoChecklist._condicao_satisfeita.
 */
function avaliarVisibilidade(
  item: TemplateItem,
  respMap: Map<string, RespostaItem>,
): boolean {
  if (!item.depende_de) return true
  const respPai = respMap.get(item.depende_de)
  if (!respPai) return false
  if (respPai.resposta_sim_nao === null || respPai.resposta_sim_nao === undefined) return false
  if (item.condicao_valor === 'sim') return respPai.resposta_sim_nao === true
  if (item.condicao_valor === 'nao') return respPai.resposta_sim_nao === false
  return true
}

export function pareItensRespostas(
  template: TemplateChecklist | undefined,
  respostas: RespostaItem[] | undefined,
): ItemComResposta[] {
  if (!template || !respostas) return []
  const respMap = new Map(respostas.map((r) => [r.item, r]))
  return template.itens
    .slice()
    .sort((a, b) => a.ordem - b.ordem)
    .map((item) => {
      const resposta = respMap.get(item.id)
      if (!resposta) return null
      return { item, resposta, visivel: avaliarVisibilidade(item, respMap) }
    })
    .filter((x): x is ItemComResposta => x !== null)
}
